import { useState } from 'react'
import { AlertCircle, X, Trash2, Copy } from 'lucide-react'
import { useUIStore, type ErrorLogEntry } from '@/stores/ui.store'

function formatTime(ts: number): string {
  const d = new Date(ts)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/**
 * Плавающая кнопка в правом нижнем углу экрана.
 * При клике показывает историю ошибок, зафиксированных на этой машине
 * (хранится в localStorage до 200 последних записей).
 */
export function ErrorHistoryFab() {
  const history = useUIStore((s) => s.errorHistory)
  const clear = useUIStore((s) => s.clearErrorHistory)
  const [open, setOpen] = useState(false)

  const count = history.length
  const hasErrors = count > 0

  const copyAll = () => {
    const text = history
      .map((e) => `[${formatTime(e.timestamp)}] ${e.url} — ${e.message}`)
      .join('\n')
    navigator.clipboard?.writeText(text).catch(() => {})
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`История ошибок (${count})`}
        className={
          'fixed bottom-4 right-4 z-[9998] flex items-center gap-1.5 rounded-full border bg-white px-3 py-2 text-xs font-medium shadow-md transition hover:shadow-lg ' +
          (hasErrors
            ? 'border-red-200 text-red-700 hover:bg-red-50'
            : 'border-gray-200 text-gray-500 hover:bg-gray-50')
        }
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Ошибки {hasErrors ? <span className="rounded-full bg-red-100 px-1.5 text-red-700">{count}</span> : null}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[9999] flex items-end justify-end bg-black/30 p-4 sm:items-center sm:justify-center"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex h-[80vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  История ошибок на этой машине
                </h2>
                <p className="text-xs text-gray-500">
                  Всего: {count} (сохраняется до {200} последних).
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={copyAll}
                  disabled={!hasErrors}
                  className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  <Copy className="h-3 w-3" />
                  Копировать всё
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Очистить историю ошибок на этой машине?')) clear()
                  }}
                  disabled={!hasErrors}
                  className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-40"
                >
                  <Trash2 className="h-3 w-3" />
                  Очистить
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100"
                  aria-label="Закрыть"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {!hasErrors ? (
                <div className="flex h-full items-center justify-center p-8 text-center text-sm text-gray-500">
                  Ошибок пока не было — всё в порядке.
                </div>
              ) : (
                <ul className="divide-y">
                  {[...history].reverse().map((e: ErrorLogEntry) => (
                    <li key={e.id} className="px-4 py-3 text-sm">
                      <div className="flex items-start justify-between gap-4">
                        <p className="flex-1 whitespace-pre-wrap text-gray-900">{e.message}</p>
                        <span className="whitespace-nowrap text-xs text-gray-400">
                          {formatTime(e.timestamp)}
                        </span>
                      </div>
                      <p className="mt-1 truncate text-xs text-gray-500">
                        {e.url}
                        {e.source ? ` · ${e.source}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
